import * as React from 'react';
import { Controller, type Control, type FieldValues } from 'react-hook-form';

import { composeRefs } from '../internals/composeRefs';
import { Toggle, type ToggleProps } from '../primitives/Toggle';
import { FormField } from '../patterns/FormField';
import { rem } from '../tokens/spacing';

import { rhfInlineError } from './rhfInlineError';
import type { RhfFormFieldShellProps, RhfLayoutProps } from './rhfLayout';

export interface RhfToggleProps extends Omit<ToggleProps, 'checked'>, RhfLayoutProps, RhfFormFieldShellProps {
  control: Control<FieldValues>;
  name: string;
  label: string;
  required?: boolean;
  semanticId?: string;
  reserveLabelSpace?: boolean;
}

export const RhfToggle = React.forwardRef<HTMLInputElement, RhfToggleProps>(
  function RhfToggle({ control, name, label, hint, required = false, semanticId, disabled, controlWidth, reserveLabelSpace, onChange, tooltip, ...rest }, ref) {
    return (
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState, formState }) => {
          const errorText = rhfInlineError(fieldState, { isSubmitted: formState.isSubmitted });
          return (
            <FormField
              label={label}
              htmlFor={name}
              hint={hint}
              tooltip={tooltip}
              required={required}
              pairOrder="control-first"
              reserveLabelSpace={reserveLabelSpace}
              semanticId={semanticId ? `${semanticId}-field` : undefined}
              disabled={disabled}
              error={errorText}
              controlWidth={controlWidth}
            >
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  minHeight: rem(24),
                }}
              >
                <Toggle
                  ref={composeRefs(field.ref, ref)}
                  id={name}
                  name={field.name}
                  checked={Boolean(field.value)}
                  onChange={(e) => {
                    onChange?.(e);
                    field.onChange(e.currentTarget.checked);
                  }}
                  onBlur={field.onBlur}
                  disabled={disabled}
                  semanticId={semanticId}
                  {...rest}
                />
              </div>
            </FormField>
          );
        }}
      />
    );
  },
);

RhfToggle.displayName = 'RhfToggle';
